// Which of the caller's brands the UI is currently acting as.
//
// A UI preference, not a permission: it only ever *narrows* what the server
// returns. Every brand-side query passes it as `brandProfileId`, and the server
// re-checks `ownerUserId` inside the same query — so a tampered value in
// localStorage reaches nothing the caller could not already read.
//
// `null` means "all brands", which for an agency is the cross-client view and
// for a single-brand account is indistinguishable from their only brand.

import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "muv.activeBrandProfileId";

// Bridges the gap `storage` events leave: they fire in *other* tabs only, so a
// switcher would update its own tab's other components only on the next render
// that happened to re-read. Subscribers listen for both.
const CHANGED = "muv:active-brand-changed";

const listeners = new Set<() => void>();

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  window.addEventListener("storage", onChange);
  window.addEventListener(CHANGED, onChange);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener("storage", onChange);
    window.removeEventListener(CHANGED, onChange);
  };
}

function getSnapshot(): string | null {
  return localStorage.getItem(STORAGE_KEY);
}

// SSR/prerender safety: there is no localStorage during a server render, and
// returning a stable value keeps useSyncExternalStore from tearing.
function getServerSnapshot(): string | null {
  return null;
}

export function useActiveBrand() {
  const activeBrandId = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const setActiveBrand = useCallback((id: string | null) => {
    if (id === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, id);
    window.dispatchEvent(new Event(CHANGED));
  }, []);

  return { activeBrandId, setActiveBrand };
}
